import { useEffect, useRef } from 'react';

import { captureLessonContext, contextFingerprint } from './live-context.service';

import type { MioContext, MioLiveContext } from '@mio/shared';

/** Silent observations update context; scrolling never creates a chat turn. */
export function useLiveContext(
  page: MioContext | null,
  tabId: string,
  onContext: (context: MioLiveContext) => void,
) {
  const current = useRef(page);
  current.current = page;
  const revision = useRef(0);
  const previous = useRef('');
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const capture = () => {
      if (!current.current) return;
      const context = captureLessonContext(current.current, tabId, revision.current + 1);
      const fingerprint = contextFingerprint(context);
      if (fingerprint === previous.current) return;
      previous.current = fingerprint;
      revision.current = context.revision;
      onContext(context);
    };
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(capture, 180);
    };
    document.addEventListener('scroll', schedule, true);
    document.addEventListener('selectionchange', schedule);
    document.addEventListener('click', schedule);
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: [
        'data-lesson-id',
        'data-lesson-version',
        'data-trace-index',
        'data-model-state',
        'data-reading-section-id',
        'data-mio-ide-attempt',
        'data-mio-ide-generation',
      ],
    });
    capture();
    return () => {
      clearTimeout(timer);
      document.removeEventListener('scroll', schedule, true);
      document.removeEventListener('selectionchange', schedule);
      document.removeEventListener('click', schedule);
      observer.disconnect();
    };
  }, [page, tabId, onContext]);
}
